/**
 * ScopingModeNotice - Lightweight notice shown in Assistant when redirected from guarded route
 */

import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertCircle, X } from 'lucide-react';

const ScopingModeNotice = ({ onDismiss }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [showNotice, setShowNotice] = useState(false);
  const [routeName, setRouteName] = useState('');
  
  useEffect(() => {
    const ref = searchParams.get('ref');
    const fromRoute = searchParams.get('from');
    
    if (ref === 'guard' && fromRoute) {
      setRouteName(formatRouteName(fromRoute));
      setShowNotice(true);
    }
  }, [searchParams]);

  const handleDismiss = () => {
    // Clear the query params
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('ref');
    newParams.delete('from');
    setSearchParams(newParams, { replace: true });
    setShowNotice(false);
    onDismiss?.();
  };

  // Format the route name for display
  const formatRouteName = (route) => {
    if (!route) return 'that page';
    const decoded = decodeURIComponent(route);
    const name = decoded.replace(/^\//, '').replace(/-/g, ' ').replace(/\//g, ' > ');
    return name.charAt(0).toUpperCase() + name.slice(1);
  };

  if (!showNotice) return null;

  return (
    <div 
      className="flex items-start gap-3 p-3 mb-4 bg-amber-50 border border-amber-200 rounded-lg"
      data-testid="scoping-mode-notice"
    >
      <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
      <div className="flex-1">
        <p className="text-sm font-medium text-amber-800">
          Page not available in Scoping Mode
        </p>
        <p className="text-xs text-amber-700 mt-1">
          You tried to open "{routeName}" which isn't available in ScopeOps Scoping Mode. 
          Use the AI Assistant for help with scoping tasks.
        </p>
      </div>
      <button
        onClick={handleDismiss}
        className="p-1 rounded hover:bg-amber-100 transition-colors"
        aria-label="Dismiss notice"
        data-testid="scoping-notice-dismiss"
      >
        <X className="w-4 h-4 text-amber-600" />
      </button>
    </div>
  );
};

export default ScopingModeNotice;
